/*
  Cart Bundle Gift Sync — keeps a PDP upsell-tier bundle line's paired free
  gift line(s) at the same quantity as the bundle line itself.
  Rules: .claude/rules/product-pdp.md ("Upsell Tier Architecture")

  Generic, not merchant-configurable: any cart line carrying `_bundle: true`
  is a purchased upsell-tier bundle unit; its paired gift line(s), if any,
  carry `_free_gift: true` and `_gift_with: <bundle line's own variant id>`
  (set by capillaire-pdp.js at add-to-cart time, alongside the bundle line,
  in the same /cart/add.js call). Editing the bundle line's quantity via the
  cart drawer's own +/- buttons does not go through that add-to-cart flow,
  so nothing previously kept an existing gift's quantity in step — this
  reconciles that quantity on every cart change instead.

  This script only ever adjusts the QUANTITY of a gift line that already
  exists — it never creates one from scratch. It has no access to a
  product's own custom.lv_free_gift metafield (cart.js exposes line items,
  not product metafields), so it has no way to know what variant a
  from-scratch gift should even be; whether a bundle has a gift at all is
  decided once, at add-to-cart time, by whether capillaire-pdp.js included
  one in that same call. An earlier version of this script assumed every
  `_bundle: true` line wants exactly one gift and, when none existed yet,
  fabricated one using the BUNDLE's own variant id as the gift id — a real,
  confirmed bug, since `_bundle: true` is set unconditionally on every
  non-self upsell tier (for the discount Function's own eligibility, see
  capillaire-pdp.js's _handleAddToCart), including giftless coffret/bundle
  tiers with no custom.lv_free_gift configured — silently doubling that
  tier's own line, self-labelled as a "free gift with itself", on every
  cart update. See .claude/rules/product-pdp.md "Tier Data JSON" for the
  full writeup.

  Server-side pricing (including the merchant-configurable per-product free
  gift limit) is owned by the free-gift-discount Shopify Function — this
  script only manages an existing gift line's quantity, never prices. If
  the bundle quantity exceeds the configured limit, the Function still only
  zeroes up to that limit; Shopify represents the remainder as a separate,
  undiscounted line in the storefront cart automatically.

  A gift line is also removed outright once its `_gift_with` variant has no
  matching cart line left at all — not just no `_bundle` line, but no line
  of ANY kind for that variant. This covers both a bundle/coffret tier fully
  removed from the cart, and a self-tier/plain-product gift whose parent
  product was removed entirely (the two look identical once the parent line
  is gone, so one check covers both). See "orphaned gift cleanup" below for
  why this can't just check for a `_bundle` line the way the top-up/trim
  logic does.
*/
(function () {
  'use strict';

  function route(path) {
    if (window.LVTheme && window.LVTheme.route) return window.LVTheme.route(path);
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return root + path.replace(/^\/+/, '');
  }

  var reconciling = false;
  var pending = false;
  var suppress = false;
  var debounceTimer = null;

  function isBundleLine(item) {
    return (item.properties || {})._bundle === 'true';
  }

  function isGiftLine(item) {
    return (item.properties || {})._free_gift === 'true';
  }

  function readCap(giftVariantId) {
    try {
      var value = parseInt(window.sessionStorage.getItem('lv_gift_cap_' + giftVariantId), 10);
      return value > 0 ? value : 0;
    } catch (e) { return 0; }
  }

  function writeCap(giftVariantId, units) {
    try { window.sessionStorage.setItem('lv_gift_cap_' + giftVariantId, String(units)); } catch (e) { /* ignore */ }
  }

  /* Remove `excess` units from gift lines, starting from the last line. */
  function trimLines(lines, excess, toChange) {
    for (var i = lines.length - 1; i >= 0 && excess > 0; i--) {
      var take = Math.min(lines[i].quantity, excess);
      excess -= take;
      toChange.push({ key: lines[i].key, quantity: lines[i].quantity - take });
    }
  }

  function reconcile() {
    if (reconciling) {
      pending = true;
      return;
    }
    reconciling = true;

    fetch(route('cart.js'))
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(function (cart) {
        /* Desired gift quantity per bundle variant id = sum of that
           variant's own bundle-line quantities (1 gift unit per bundle
           unit — the only ratio the PDP tier system currently produces). */
        var desiredByVariantId = {};
        cart.items.forEach(function (item) {
          if (!isBundleLine(item)) return;
          var variantId = String(item.variant_id);
          desiredByVariantId[variantId] = (desiredByVariantId[variantId] || 0) + item.quantity;
        });

        /* Actual gift lines per `_gift_with` variant id — Shopify may have
           split a single logical gift into more than one cart line once a
           per-product free-gift limit made only part of its quantity
           free, so this must aggregate across every matching line. */
        var giftLinesByVariantId = {};
        cart.items.forEach(function (item) {
          if (!isGiftLine(item)) return;
          var giftWith = (item.properties || {})._gift_with;
          if (!giftWith) return;
          giftWith = String(giftWith);
          if (!giftLinesByVariantId[giftWith]) giftLinesByVariantId[giftWith] = [];
          giftLinesByVariantId[giftWith].push(item);
        });

        var toChange = [];

        /* Only ever touch gift lines whose `_gift_with` variant currently
           has a matching `_bundle` line in the cart. A gift line with no
           `_bundle` counterpart at all is the *other*, pre-existing gift
           mechanism (a gift tied directly to a plain product purchase, not
           to an upsell-tier bundle — e.g. the brush's "2 free gifts with
           purchase") and must be left completely alone. Iterating the
           union with giftLinesByVariantId here (instead of just
           desiredByVariantId's own keys) was a real, shipped bug: it made
           this watcher treat every such self-tier gift as "no bundle
           justifies this" and delete it moments after it was added. */
        Object.keys(desiredByVariantId).forEach(function (variantId) {
          var desired = desiredByVariantId[variantId];
          var lines = giftLinesByVariantId[variantId] || [];
          var actual = lines.reduce(function (sum, line) { return sum + line.quantity; }, 0);

          if (actual === desired) return;

          if (actual < desired) {
            /* Only ever top up an EXISTING gift line's quantity — never
               fabricate a brand-new one from scratch. This script has no
               access to the bundle product's own custom.lv_free_gift
               metafield (cart.js exposes line items, not product
               metafields), so it cannot know what variant a from-scratch
               gift should even be. The `else` branch that used to run here
               assumed "the gift is the same variant as the bundle itself"
               and pushed `{ id: Number(variantId), ... }` using the
               BUNDLE's own variant id as the gift id — a real, confirmed
               bug: every giftless upsell tier (a coffret/bundle with no
               custom.lv_free_gift configured at all) still carries
               `_bundle: 'true'` on its cart line (set unconditionally for
               any non-self tier, for the discount Function's own
               eligibility — see capillaire-pdp.js's _handleAddToCart), so
               this watcher treated it exactly like a bundle that DOES want
               a gift and silently added a second line of that same
               product, self-labelled as a "free gift with itself".
               Whether a gift should exist at all is established once, at
               add-to-cart time (capillaire-pdp.js adds the bundle line and
               its real gift line(s), if any, together in one call) — if no
               gift line was created then, none should be invented later.
               A gift line lost entirely to a transient failure (e.g. a 422
               on the original add) is a rarer, separate case; self-healing
               that would require this script to read product metafields
               too, not assume identity with the bundle's own variant. */
            if (lines.length) {
              var diff = desired - actual;
              toChange.push({ key: lines[0].key, quantity: lines[0].quantity + diff });
            }
            return;
          }

          /* actual > desired: remove the excess, starting from the last
             line so an existing first line (and its key) survives. */
          var excess = actual - desired;
          for (var i = lines.length - 1; i >= 0 && excess > 0; i--) {
            var line = lines[i];
            var take = Math.min(line.quantity, excess);
            excess -= take;
            var newQty = line.quantity - take;
            toChange.push({ key: line.key, quantity: newQty });
          }
        });

        /* Gifts of a PLAIN product (no `_bundle` flag), e.g. "2 Spray it up +
           1 Gratuit" bought twice: the PDP adds 1 gift unit per product unit
           (self tier), but nothing used to keep that in step when the
           quantity changes in the drawer, so 2 kits kept 1 free gift. Same
           1:1 rule as the bundle logic above, applied per (parent variant,
           gift variant) pair so a parent with several different gifts is
           never mixed up.

           Safety: a gift can only be raised while it is actually free. The
           free-gift discount may cover fewer units than the line quantity
           (a per-product cap, or an old Function version that only frees
           one unit); in that case the paid extra is trimmed back and the
           free count is remembered for the session so this never loops or
           leaves a paid unit labelled "offert". */
        var plainQtyByVariantId = {};
        cart.items.forEach(function (item) {
          if (isGiftLine(item) || isBundleLine(item)) return;
          var vid = String(item.variant_id);
          plainQtyByVariantId[vid] = (plainQtyByVariantId[vid] || 0) + item.quantity;
        });

        var plainGroups = {};
        cart.items.forEach(function (item) {
          if (!isGiftLine(item)) return;
          var parent = (item.properties || {})._gift_with;
          if (!parent) return;
          parent = String(parent);
          if (desiredByVariantId[parent] !== undefined) return; /* bundle logic owns it */
          if (!plainQtyByVariantId[parent]) return; /* orphan: cleaned up below */
          var key = parent + '|' + item.variant_id;
          if (!plainGroups[key]) plainGroups[key] = { parent: parent, giftVariantId: String(item.variant_id), lines: [] };
          plainGroups[key].lines.push(item);
        });

        Object.keys(plainGroups).forEach(function (key) {
          var group = plainGroups[key];
          var lines = group.lines;
          var desired = plainQtyByVariantId[group.parent];
          var actual = 0;
          var freeUnits = 0;
          lines.forEach(function (line) {
            actual += line.quantity;
            var unit = line.original_price;
            freeUnits += unit > 0
              ? Math.round((line.original_line_price - line.final_line_price) / unit)
              : line.quantity;
          });

          if (freeUnits < actual) {
            /* Part of the gift is being charged: keep only what is free. */
            if (freeUnits > 0) {
              writeCap(group.giftVariantId, freeUnits);
              trimLines(lines, actual - freeUnits, toChange);
            }
            return;
          }

          var cap = readCap(group.giftVariantId);
          var target = cap ? Math.min(desired, cap) : desired;

          if (actual < target) {
            toChange.push({ key: lines[0].key, quantity: lines[0].quantity + (target - actual) });
          } else if (actual > target) {
            trimLines(lines, actual - target, toChange);
          }
        });

        /* Orphaned gift cleanup: remove a gift line outright once its
           `_gift_with` variant has no cart line left AT ALL — bundle or
           not. This is deliberately a different, broader check than the
           `_bundle`-only one above (`presentVariantIds` below includes
           every non-gift line, self-tier/plain-product lines included),
           and that difference is exactly what avoids reintroducing the
           real, shipped bug described above: that bug came from treating
           "no matching `_bundle` line" as "orphaned", which is wrong for a
           self-tier gift — its `_gift_with` points at the SELF product's
           own variant id, and that line is still very much in the cart,
           just never flagged `_bundle` (only non-self tiers get that flag).
           Checking "no line of any kind" instead of "no `_bundle` line"
           correctly leaves that case alone (the self product's line keeps
           its variant id present) while correctly catching the case this
           was missing: a bundle/coffret removed entirely, or a plain
           product (e.g. the brush) removed entirely, leaves its gift
           behind with no parent line of any kind — that gift should go
           too, per the user-confirmed rule "no product in cart, no gift
           either." A variant already handled by the loop above (still
           desired, top-up/trim) is by definition present, so this can't
           double-touch those lines — it only ever reaches variants that
           loop never visited. */
        var presentVariantIds = {};
        cart.items.forEach(function (item) {
          if (isGiftLine(item)) return;
          presentVariantIds[String(item.variant_id)] = true;
        });

        Object.keys(giftLinesByVariantId).forEach(function (variantId) {
          if (presentVariantIds[variantId]) return;
          giftLinesByVariantId[variantId].forEach(function (line) {
            toChange.push({ key: line.key, quantity: 0 });
          });
        });

        if (!toChange.length) return null; /* fixpoint */

        suppress = true;
        var ops = Promise.resolve();

        toChange.forEach(function (change) {
          ops = ops.then(function () { return changeItem(change.key, change.quantity); });
        });

        return ops.then(function () {
          if (window.cartDrawer && window.cartDrawer.refresh) return window.cartDrawer.refresh();
        });
      })
      .catch(function () { /* network error — retry on the next cart:updated */ })
      .then(function () {
        suppress = false;
        reconciling = false;
        if (pending) {
          pending = false;
          reconcile();
        }
      });
  }

  function changeItem(key, quantity) {
    if (window.LVTheme && window.LVTheme.cart) {
      return window.LVTheme.cart.changeItem(key, quantity, { refreshDrawer: false });
    }
    return fetch(route('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (response) {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    });
  }

  document.addEventListener('cart:updated', function () {
    if (suppress) return;
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(reconcile, 150);
  });
})();
